// Core
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

// Libraries
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import dayjs from 'dayjs';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Plugins
import { formatNumber } from '../../../plugins/general';

// Types
import { Theme } from '../../../types/theme';
import { MeasurementType, TimeUnitType } from '../../../types/global';

// Component
import {
  BarChartProps,
  BarChartTheme,
  SeriesColorEnum,
  Series,
} from './BarChart.types';
import { Element } from './BarChart.style';

/* ************************** RESPONSIVE CHART RULES ************************** */

// Breakpoint to apply responsive rules (below lg = 992px)
const RESPONSIVE_CHART_BREAKPOINT: number = 992;

// Responsive values for mobile/tablet screens
const CHART_MOBILE_RULES = {
  xAxis: { fontSize: 11, paddingTop: 8 },
  yAxis: { fontSize: 10 },
  unit: { fontSize: 10, height: 18 },
  chart: { paddingTop: 30 },
} as const;

// Original desktop values (restored when resizing back up)
const CHART_DESKTOP_RULES = {
  xAxis: { fontSize: 14, paddingTop: 16 },
  yAxis: { fontSize: 12 },
  unit: { fontSize: 12, height: 20 },
  chart: { paddingTop: 35 },
} as const;

function BarChart({
  className,
  data,
  id,
  series,
  testId,
}: BarChartProps): JSX.Element {
  // Dependencies
  const theme: Theme = useTheme();
  const { i18n } = useTranslation();

  // References for chart and root
  const rootRef: React.MutableRefObject<am5.Root | null> = useRef<am5.Root | null>(null);
  const chartRef: React.MutableRefObject<am5xy.XYChart | null> = useRef<am5xy.XYChart | null>(null);
  const isFirstRender: React.MutableRefObject<boolean> = useRef<boolean>(true);

  /* ************************** THEME CONFIGURATION ************************** */

  const themeConfig = useMemo<BarChartTheme>(
    () => ({
      colors: {
        barSeriesColor: {
          blue: theme.color.barChart.seriesColorBlue,
          green: theme.color.barChart.seriesColorGreen,
          lightBlue: theme.color.barChart.seriesColorLightBlue,
          lightGreen: theme.color.barChart.seriesColorLightGreen,
          lightOrange: theme.color.barChart.seriesColorLightOrange,
          lightPink: theme.color.barChart.seriesColorLightPink,
          orange: theme.color.barChart.seriesColorOrange,
          pink: theme.color.barChart.seriesColorPink,
          purple: theme.color.barChart.seriesColorPurple,
          yellow: theme.color.barChart.seriesColorYellow,
        },
      },
    }),
    [theme],
  );

  const convertedUnit = useCallback((
    unit: TimeUnitType | string | undefined,
    measurementType: MeasurementType | undefined,
  ): string => {
    let unitLabel: string | undefined = unit;
    if (measurementType === MeasurementType.time) {
      unitLabel = `${i18n.t(`dates.timeUnit.${unit}.plural`)}`;
    }
    return unitLabel || '';
  }, [i18n]);

  /* ************************** DYNAMIC SCALE HELPERS ************************** */

  /**
   * Calculates the minimum and maximum values from data for a specific field
   */
  const calculateDataMinMax = useCallback((
    chartData: Record<string, unknown>[],
    field: string,
  ): { min: number | undefined; max: number | undefined } => {
    const values: number[] = chartData
      .map((item: Record<string, unknown>) => item[field])
      .filter((value: unknown) => typeof value === 'number' && !Number.isNaN(value))
      .map((value: unknown) => value as number);

    if (values.length === 0) {
      return { min: undefined, max: undefined };
    }

    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, []);

  /**
   * Calculates global min/max for all series that share the same unit and measurement type
   */
  const calculateGlobalMinMaxForUnit = useCallback((
    processedData: Record<string, unknown>[],
    seriesConfigs: Series[],
  ): { min: number | undefined; max: number | undefined } => {
    let globalMin: number | undefined;
    let globalMax: number | undefined;

    seriesConfigs.forEach((config: Series) => {
      const fieldMinMax: {
        min: number | undefined;
        max: number | undefined;
      } = calculateDataMinMax(processedData, config.field);

      if (fieldMinMax.min !== undefined) {
        globalMin = globalMin === undefined
          ? fieldMinMax.min
          : Math.min(globalMin, fieldMinMax.min);
      }

      if (fieldMinMax.max !== undefined) {
        globalMax = globalMax === undefined
          ? fieldMinMax.max
          : Math.max(globalMax, fieldMinMax.max);
      }
    });

    return { min: globalMin, max: globalMax };
  }, [calculateDataMinMax]);

  /**
   * Calculates a dynamic and proportional margin based on the magnitude of the values
   */
  const calculateDynamicMargin = useCallback((
    min: number,
    max: number,
    configuredMin: number,
    configuredMax: number,
  ): number => {
    const dataRange: number = Math.abs(max - min);
    const configuredRange: number = Math.abs(configuredMax - configuredMin);
    const baseRange: number = Math.max(dataRange, configuredRange);

    // Special case: when min == max (all values are equal)
    if (dataRange === 0) {
      if (Math.abs(max) < 0.0001) {
        return configuredRange * 0.15;
      }
      return Math.max(Math.abs(max) * 0.15, configuredRange * 0.15);
    }

    // Special case: very small values (close to zero)
    if (baseRange < 0.001) {
      return baseRange * 0.2;
    }

    // Special case: small values (< 1)
    if (baseRange < 1) {
      return baseRange * 0.15;
    }

    // Special case: large values (> 1000)
    if (baseRange > 1000) {
      return baseRange * 0.1;
    }

    // General case: values between 1 and 1000
    return baseRange * 0.12;
  }, []);

  /**
   * Updates the Y-axis scale dynamically based on current data
   */
  const updateYAxisScale = useCallback((
    yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
    processedData: Record<string, unknown>[],
    config: Series,
  ): void => {
    if (!processedData || processedData.length === 0) {
      if (config.yAxisMax !== undefined) {
        yAxis.set('max', config.yAxisMax);
      }
      if (config.yAxisMin !== undefined) {
        yAxis.set('min', config.yAxisMin);
      }
      return;
    }

    // Find all series that share the same unit and measurement type
    const relatedSeries: Series[] = series.filter((s: Series) => (
      s.unit === config.unit && s.measurementType === config.measurementType
    ));

    // Calculate global min/max considering ALL related series
    const globalMinMax: {
      min: number | undefined;
      max: number | undefined;
    } = calculateGlobalMinMaxForUnit(processedData, relatedSeries);

    // If no valid data, use configured values
    if (globalMinMax.min === undefined || globalMinMax.max === undefined) {
      if (config.yAxisMax !== undefined) {
        yAxis.set('max', config.yAxisMax);
      }
      if (config.yAxisMin !== undefined) {
        yAxis.set('min', config.yAxisMin);
      }
      return;
    }

    const dataMin: number = globalMinMax.min;
    const dataMax: number = globalMinMax.max;

    // Configured values
    const configuredMin: number = config.yAxisMin ?? dataMin;
    const configuredMax: number = config.yAxisMax ?? dataMax;

    // Calculate dynamic margin
    const dynamicMargin: number = calculateDynamicMargin(
      dataMin,
      dataMax,
      configuredMin,
      configuredMax,
    );

    // LOGIC FOR MINIMUM VALUE (keep it simple - use configured value)
    const finalMin: number = config.yAxisMin ?? (dataMin - dynamicMargin);

    // LOGIC FOR MAXIMUM VALUE
    let finalMax: number;

    if (config.yAxisMax !== undefined) {
      // If dataMax exceeds configured max, expand dynamically
      if (dataMax > configuredMax) {
        finalMax = dataMax + dynamicMargin;
      } else if (Math.abs(dataMax - configuredMax) < dynamicMargin * 0.1) {
        // If dataMax is very close to configured max, apply margin
        finalMax = dataMax + dynamicMargin;
      } else {
        // Data is within range, keep configured value
        finalMax = configuredMax;
      }
    } else {
      // If no yAxisMax configured, always use dynamic scaling
      finalMax = dataMax + dynamicMargin;
    }

    // VALIDATION: Ensure min < max
    if (finalMin >= finalMax) {
      const adjustment: number = dynamicMargin || 1;
      finalMax = finalMin + adjustment;
    }

    // Apply calculated values to Y-axis
    yAxis.set('min', finalMin);
    yAxis.set('max', finalMax);
  }, [series, calculateGlobalMinMaxForUnit, calculateDynamicMargin]);

  // Configure the X axis
  const configureXAxis = useCallback((
    root: am5.Root,
    chart: am5xy.XYChart,
  ): am5xy.CategoryAxis<am5xy.AxisRendererX> => {
    const xRenderer: am5xy.AxisRendererX = am5xy.AxisRendererX.new(root, { minGridDistance: 1 });

    // Category captions
    xRenderer.labels.template.setAll({
      fontSize: 14,
      fill: am5.color(theme.color.barChart.axisColor),
      paddingTop: 16,
      fontFamily: theme.font.regular,
      centerX: am5.percent(50),
      location: 0.5,
    });
    xRenderer.grid.template.setAll({ visible: false, strokeOpacity: 0 });

    const xAxis: am5xy.CategoryAxis<am5xy.AxisRendererX> = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, { categoryField: 'category', renderer: xRenderer }),
    );

    return xAxis;
  }, [theme]);

  // Configure the Y axis
  const configureYAxis = useCallback((
    root: am5.Root,
    chart: am5xy.XYChart,
    seriesConfig: Series,
    index: number,
  ): am5xy.ValueAxis<am5xy.AxisRenderer> => {
    const yRenderer: am5xy.AxisRendererY = am5xy.AxisRendererY.new(root, { strokeOpacity: 0 });
    yRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(theme.color.barChart.axisColor),
    });

    // Render additional Y-axes on the opposite (right) side
    if (index > 0) {
      yRenderer.set('opposite', true);
    }

    // Create the Y-axis using the seriesConfig
    // Note: max is not set here, it will be set dynamically by updateYAxisScale
    const yAxis: am5xy.ValueAxis<am5xy.AxisRenderer> = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yRenderer,
        min: seriesConfig?.yAxisMin,
      }),
    );

    // Add unit label to Y-axis
    const unitLabel: am5.Label = am5.Label.new(root, {
      rotation: 0,
      text: convertedUnit(seriesConfig?.unit, seriesConfig?.measurementType),
      x: am5.percent(50),
      y: -25,
      width: am5.percent(1),
      height: 50,
      fontFamily: theme.font.medium,
      fontSize: 12,
      fill: am5.color(theme.color.barChart.unitColor),
      centerY: am5.percent(50),
      textAlign: 'center',
      textBaseline: 'middle',
    });

    yAxis.children.push(unitLabel);

    // Configure Y-axis labels using decimals
    yAxis.get('renderer').labels.template.adapters.add(
      'text',
      (
        text: string | undefined,
        target: am5xy.AxisLabel,
      ) => {
        const value: number = target.dataItem?.get('value' as any);
        if (value === undefined || value === null) {
          return text;
        }

        // Custom format for MeasurementType.time
        if (seriesConfig?.measurementType === MeasurementType.time) {
          return dayjs(value).format('mm:ss');
        }
        const decimals: number = seriesConfig?.decimals || 0;
        return value !== undefined ? formatNumber(value, decimals) : text;
      },
    );

    return yAxis;
  }, [theme, convertedUnit]);

  /* ************************** SERIES CREATION ************************** */

  const createSeries = useCallback((
    root: am5.Root,
    chart: am5xy.XYChart,
    xAxis: am5xy.CategoryAxis<am5xy.AxisRendererX>,
    yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
    seriesConfig: Series,
    index: number,
  ): void => {
    const defaultSeriesColor: string[] = Object.values(themeConfig.colors.barSeriesColor);

    const {
      name,
      field,
      color,
      unit,
      decimals,
      measurementType,
    } = seriesConfig;

    // Configuration of the series with disabled animations for static behavior
    const columnSeries: am5xy.ColumnSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: unit ? `${name} (${convertedUnit(unit, measurementType)})` : name,
        xAxis,
        yAxis,
        valueYField: field,
        categoryXField: 'category',
      }),
    );

    let tooltipTextTemplate: string = `[bold]{name}[/]: {valueY} ${unit || ''}`;

    // If it's time, remove the extra unit (ex: 'minute')
    if (measurementType === MeasurementType.time) {
      tooltipTextTemplate = '[bold]{name}[/]: {valueY}';
    }

    columnSeries.columns.template.setAll({
      tooltipText: tooltipTextTemplate,
      width: am5.percent(25),
      tooltipY: 0,
      strokeOpacity: 0,
      cornerRadiusTL: 4,
      cornerRadiusTR: 4,
      cornerRadiusBL: 4,
      cornerRadiusBR: 4,
      fill: am5.color(
        themeConfig.colors.barSeriesColor[
          color as SeriesColorEnum
        ] || defaultSeriesColor[index],
      ),
    });

    columnSeries.columns.template.adapters.add('tooltipText', (
      text: string | undefined,
      target: am5.RoundedRectangle,
    ) => {
      const value: number = target.dataItem?.get('valueY' as any) as number;
      if (value === undefined || value === null) {
        return text;
      }

      let formattedValue: string;

      if (measurementType === MeasurementType.time) {
        const mapTimeUnit: Partial<Record<TimeUnitType, string>> = {
          [TimeUnitType.millisecond]: 'hh:mm:ss.SSS',
          [TimeUnitType.second]: 'mm:ss',
          [TimeUnitType.minute]: 'mm:ss',
          [TimeUnitType.hour]: 'HH:mm:ss',
          [TimeUnitType.day]: 'dd:hh:mm:ss',
        };
        formattedValue = dayjs(value).format(mapTimeUnit[unit as TimeUnitType]);
      } else {
        formattedValue = formatNumber(value, decimals || 0);
      }

      return text?.replace('{valueY}', formattedValue);
    });
  }, [themeConfig, convertedUnit]);

  /**
   * Animate column value with smooth animation transition
   *
   * @param columnSeries - The column series that is being updated
   * @param categoryName - The name of the category (value of the X axis) to be updated
   * @param newValue - The new numeric value for the column
   * @param duration - The duration of the animation in milliseconds (default: 800)
   */
  const updateAnimatedColumn = useCallback((
    columnSeries: am5xy.ColumnSeries,
    categoryName: string,
    newValue: number,
    duration: number = 800,
  ): void => {
    const dataItem: am5.DataItem<am5xy.IColumnSeriesDataItem> | undefined = (
      columnSeries.dataItems.find(
        (item: am5.DataItem<am5xy.IColumnSeriesDataItem>) => item.get('categoryX') === categoryName,
      )
    );

    if (dataItem) {
      const oldValue: number = dataItem.get('valueY') as number || 0;

      dataItem.set('valueY', newValue);

      dataItem.animate({
        key: 'valueYWorking',
        to: newValue,
        from: oldValue,
        duration,
        easing: am5.ease.out(am5.ease.cubic),
      });
    }
  }, []);

  const renderChart = useCallback((): void => {
    if (!rootRef.current || chartRef.current) return;

    const root: am5.Root = rootRef.current;
    root.setThemes([am5themes_Animated.new(root)]);

    const chart: am5xy.XYChart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        pinchZoomX: false,
        paddingTop: 35,
        layout: root.verticalLayout,
      }),
    );
    chartRef.current = chart;

    // Configure the X-axis (shared by all series)
    const xAxis: am5xy.CategoryAxis<am5xy.AxisRendererX> = configureXAxis(root, chart);

    // Iterate over each series configuration and create independent Y-axis and series
    series.forEach((seriesConfig: Series, index: number) => {
      const yAxis: am5xy.ValueAxis<am5xy.AxisRenderer> = configureYAxis(
        root,
        chart,
        seriesConfig,
        index,
      );
      createSeries(root, chart, xAxis, yAxis, seriesConfig, index);
    });

    chart.appear(1000, 100);

    // Responsive rules: adjust fonts/spacing for mobile/tablet
    chart.onPrivate('width', (width: number | undefined): void => {
      const isSmallScreen: boolean = (width ?? 0) < RESPONSIVE_CHART_BREAKPOINT;
      const rules: typeof CHART_MOBILE_RULES | typeof CHART_DESKTOP_RULES = (
        isSmallScreen ? CHART_MOBILE_RULES : CHART_DESKTOP_RULES
      ) as typeof CHART_MOBILE_RULES | typeof CHART_DESKTOP_RULES;

      // Adjust X axis labels
      chart.xAxes.each((xAxisItem) => {
        xAxisItem.get('renderer').labels.template.setAll({
          fontSize: rules.xAxis.fontSize,
          paddingTop: rules.xAxis.paddingTop,
        });
      });

      // Adjust Y axes labels and unit labels
      chart.yAxes.each((yAxisItem) => {
        yAxisItem.get('renderer').labels.template.setAll({
          fontSize: rules.yAxis.fontSize,
        });

        // Adjust unit labels (identified by y === -25)
        yAxisItem.children.each((child) => {
          if (child instanceof am5.Label && child.get('y') === -25) {
            child.setAll({
              fontSize: rules.unit.fontSize,
              height: rules.unit.height,
            });
          }
        });
      });

      // Adjust chart padding
      chart.setAll({
        paddingTop: rules.chart.paddingTop,
      });
    });
  }, [
    series,
    configureXAxis,
    configureYAxis,
    createSeries,
  ]);

  /* ***********************************************************************************************
  ***************************************** COMPONENT HANDLING *************************************
  *********************************************************************************************** */

  useEffect(() => {
    // Check if the rootRef is not already initialized
    if (!rootRef.current) {
      // useSafeResolution: false → disables AmCharts' forced resolution=1 on iOS
      // By default AmCharts sets resolution=1 on iOS (ignoring devicePixelRatio)
      // to save memory, but this causes blurry text on Retina displays
      rootRef.current = am5.Root.new(id, { useSafeResolution: false });
    }

    // Cleanup function to dispose of the rootRef and reset references
    return (): void => {
      if (rootRef.current) {
        // Dispose of the am5.Root instance
        rootRef.current.dispose();
        // Reset the rootRef and chartRef to null
        rootRef.current = null;
        chartRef.current = null;
      }
    };
  }, [id]);

  useEffect((): void => {
    renderChart();
    isFirstRender.current = true;
  }, [renderChart, i18n.language]);

  useEffect((): void => {
    if (chartRef.current) {
      const xAxis: am5xy.Axis<am5xy.AxisRenderer> | undefined = chartRef.current.xAxes.getIndex(0);

      // Update Y-axis scales based on current data
      chartRef.current.yAxes.each((yAxis, index) => {
        const seriesConfig = series[index];
        if (seriesConfig) {
          updateYAxisScale(yAxis as am5xy.ValueAxis<am5xy.AxisRenderer>, data, seriesConfig);
        }
      });

      // Load all data on the first render
      if (isFirstRender.current) {
        xAxis?.data.setAll(data);
        chartRef.current.series.each((seriesChart) => {
          seriesChart.data.setAll(data);
        });
        isFirstRender.current = false;
      } else {
        // Animate each column individually on subsequent updates
        chartRef.current.series.each((seriesChart, seriesIndex) => {
          const columnSeries: am5xy.ColumnSeries = seriesChart as am5xy.ColumnSeries;
          const seriesConfig: Series = series[seriesIndex];

          if (seriesConfig) {
            // Iterate over each data point and animate the transition
            data.forEach((dataPoint: Record<string, any>) => {
              const categoryName: string = dataPoint.category;
              const newValue: number = dataPoint[seriesConfig.field];

              if (newValue !== undefined && newValue !== null) {
                updateAnimatedColumn(columnSeries, categoryName, newValue);
              }
            });
          }
        });
      }
    }
  }, [data, series, updateAnimatedColumn, updateYAxisScale]);

  return (
    <Element
      id={id}
      data-testid={testId}
      className={className}
    />
  );
}

export { BarChart };
